/**
 * Persistence Bridge - Infrastructure coordinator for project content sync.
 *
 * ARCHITECTURE:
 *   projectStore → persistenceEngine → eventBus → PersistenceBridge
 *     → OfflineJournal → PersistenceQueue → RetryOrchestrator
 *     → ReconciliationStrategy → SupabaseAdapter
 *
 * LIFECYCLE (as RuntimeModule):
 *   init():     Register event listeners, flush any unsynced journal entries
 *   teardown(): Flush queue immediately, remove all listeners
 *
 * LOCAL vs CLOUD:
 *   Local writes are SYNCHRONOUS and happen in projectStore before this bridge
 *   is involved. This bridge ONLY manages the async cloud sync path.
 *
 * FLUSH TRIGGERS (bypass 5s debounce):
 *   - runtime:shutdown event
 *   - window visibilitychange → hidden
 *   - window blur (additional safety net)
 *   - manual: persistenceBridge.flushNow()
 */

import { eventBus } from "@/runtime/eventBus";
import { logger } from "@/system/logger";
import { supabase } from "@/integrations/supabase/client";
import { useProjectStore } from "@/store/projectStore";
import { CURRENT_SCHEMA_VERSION, supabaseAdapter, type ProjectContentPayload } from "./storageAdapter";
import { getCanonicalSchema } from "@/core/schema/canonicalProvider";
import { runMigrations, payloadNeedsMigration, CURRENT_PAYLOAD_VERSION, SCOPE_PAYLOAD } from "./migration/index";
import { SyncLedger } from "./syncLedger";
import { OfflineJournal } from "./offlineJournal";
import { PersistenceQueue } from "./persistenceQueue";
import { RetryOrchestrator } from "./retryOrchestrator";
import { sequenceWinsStrategy } from "./reconciliation/sequenceWinsStrategy";
import "./reconciliation/timestampWinsStrategy"; // ensure registered
import type { RuntimeModule } from "@/runtime/coreRuntime";

// Suppress unused import warning - sequenceWinsStrategy is the default and is
// registered as a side effect of the import above.
void sequenceWinsStrategy;

// Crypto-safe entry ID generation
function newEntryId(): string {
  return `je-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/* =========================================================
   BRIDGE
   ========================================================= */

class PersistenceSyncBridge {
  private queue        = new PersistenceQueue((entries) => this.processEntries(entries));
  private orchestrator = new RetryOrchestrator();
  private unsubs:      (() => void)[] = [];

  // Event listeners on window - stored for removal on teardown
  private onOnline       = () => { this.queue.resume(); };
  private onOffline      = () => { this.handleOffline(); };
  private onBlur         = () => { this.flushNow("blur"); };
  private onVisibility   = () => {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      this.flushNow("visibility-hidden");
    }
  };

  /* ── INIT ──────────────────────────────────────────────── */

  init(): void {
    // Event bus subscriptions
    this.unsubs.push(
      eventBus.on("state:saved",          (e) => this.onStateSaved(e)),
      eventBus.on("persistence:local_miss",(e) => this.onLocalMiss(e)),
      eventBus.on("runtime:shutdown",      ()  => this.flushNow("runtime-shutdown")),
    );

    // Window lifecycle
    if (typeof window !== "undefined") {
      window.addEventListener("online",           this.onOnline);
      window.addEventListener("offline",          this.onOffline);
      window.addEventListener("blur",             this.onBlur);
      document.addEventListener("visibilitychange", this.onVisibility);

      // Reflect initial network state
      if (typeof navigator !== "undefined" && !navigator.onLine) this.queue.pause();
    }

    // CRASH RECOVERY: re-enqueue any unsynced journal entries from previous session
    this.replayUnsyncedJournal();

    logger.info("PersistenceBridge initialized");
  }

  /* ── TEARDOWN ──────────────────────────────────────────── */

  teardown(): void {
    // Flush immediately (best-effort before shutdown)
    void this.queue.flushAll();

    // Remove eventBus subscriptions
    this.unsubs.forEach(fn => fn());
    this.unsubs = [];

    // Remove window event listeners
    if (typeof window !== "undefined") {
      window.removeEventListener("online",            this.onOnline);
      window.removeEventListener("offline",           this.onOffline);
      window.removeEventListener("blur",              this.onBlur);
      document.removeEventListener("visibilitychange",this.onVisibility);
    }

    logger.info("PersistenceBridge torn down");
  }

  /* ── EXTERNAL API ──────────────────────────────────────── */

  /** Force an immediate flush of the queue (called from UI on manual save, deploy, etc.). */
  async flushNow(reason: string): Promise<void> {
    logger.debug("PersistenceBridge flush triggered", { reason });
    await this.queue.flushAll();
  }

  /* ── EVENT HANDLERS ────────────────────────────────────── */

  private onStateSaved(event: { type: "state:saved"; projectId: string }): void {
    const store   = useProjectStore.getState();
    const snapshot = store.getSnapshot();
    const canonicalSchema = getCanonicalSchema() ?? undefined;

    // Build the full content payload - canonical schema included when available
    const payload: ProjectContentPayload = {
      projectId:       event.projectId,
      userId:          "", // filled at sync time from supabase.auth.getUser()
      pages:           snapshot.pages,
      blocks:          snapshot.blocks,
      schema:          snapshot.schema,
      workflows:       snapshot.workflows,
      designSystem:    store.designSystem,
      settings:        store.settings as unknown as Record<string, unknown>,
      files:           snapshot.files,
      sequenceNum:     SyncLedger.get(event.projectId).localSequenceNum,
      schemaVersion:   CURRENT_SCHEMA_VERSION,
      savedAt:         Date.now(),
      canonicalSchema, // undefined for v1 payloads; populated from bridge on v2+
    };

    // Emit observability event when canonical schema is included
    if (canonicalSchema) {
      eventBus.emit({
        type:        "persistence:canonical_saved",
        projectId:   event.projectId,
        fingerprint: canonicalSchema.fingerprint,
      });
    }

    // Journal first (sync, fast)
    OfflineJournal.append({
      entryId:       newEntryId(),
      projectId:     event.projectId,
      sequenceNum:   payload.sequenceNum,
      schemaVersion: CURRENT_SCHEMA_VERSION,
      snapshot:      payload,
      savedAt:       payload.savedAt,
      syncedAt:      null,
      attempts:      0,
      lastError:     null,
    });
    eventBus.emit({ type: "persistence:journal_appended", projectId: event.projectId, sequenceNum: payload.sequenceNum });

    // Enqueue for async cloud sync
    this.queue.enqueue(payload);
    eventBus.emit({ type: "persistence:queue_enqueued", projectId: event.projectId, sequenceNum: payload.sequenceNum });
  }

  private async onLocalMiss(event: { type: "persistence:local_miss"; projectId: string }): Promise<void> {
    logger.info("PersistenceBridge: local miss - attempting cloud restore", { projectId: event.projectId });

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      logger.debug("PersistenceBridge: no auth user - skip cloud restore");
      return;
    }

    const result = await supabaseAdapter.read(event.projectId, user.id);

    if (!result.found || !result.payload) {
      logger.info("PersistenceBridge: no cloud content found for project", { projectId: event.projectId });
      eventBus.emit({ type: "persistence:cloud_load_failed", projectId: event.projectId, reason: "Not found in cloud" });
      return;
    }

    logger.info("PersistenceBridge: restoring project from cloud", {
      projectId:        event.projectId,
      sequenceNum:      result.payload.sequenceNum,
      hasCanonical:     !!result.payload.canonicalSchema,
    });

    // Phase 2.5: signal canonical schema availability BEFORE cloud_restore triggers
    // restoreFromCloud(). The bridge's persistence:canonical_available handler will
    // pre-populate currentSchema so initSchemaFromStore() uses canonical-first shortcut.
    if (result.payload.canonicalSchema) {
      eventBus.emit({
        type:        "persistence:cloud_canonical_restored",
        projectId:   event.projectId,
        fingerprint: result.payload.canonicalSchema.fingerprint,
      });
    }

    // Emit event - projectStore listens and restores itself
    eventBus.emit({
      type:        "persistence:cloud_restore",
      projectId:   event.projectId,
      snapshot:    result.payload,
      sequenceNum: result.payload.sequenceNum,
    });

    eventBus.emit({
      type:        "persistence:loaded_from_cloud",
      projectId:   event.projectId,
      sequenceNum: result.payload.sequenceNum,
    });
  }

  /* ── SYNC PROCESSING ───────────────────────────────────── */

  private async processEntries(entries: { payload: ProjectContentPayload; enqueuedAt: number }[]): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      logger.warn("PersistenceBridge: no auth user - skipping cloud sync", { count: entries.length });
      return;
    }

    for (const { payload } of entries) {
      await this.syncEntry({ ...payload, userId: user.id });
    }
  }

  private async syncEntry(payload: ProjectContentPayload): Promise<void> {
    const ctx = { projectId: payload.projectId, sequenceNum: payload.sequenceNum };

    eventBus.emit({ type: "persistence:sync_start", projectId: payload.projectId, sequenceNum: payload.sequenceNum });

    const startMs = Date.now();

    await this.orchestrator.execute(
      ctx,
      () => supabaseAdapter.write(payload).then(result => {
        if (!result.success) throw new Error(result.error ?? "Write returned failure");
        return result;
      }),
      {
        onSuccess: (c, attempt) => {
          const durationMs = Date.now() - startMs;
          OfflineJournal.markSynced(c.projectId, c.sequenceNum);
          SyncLedger.markSynced(c.projectId, c.sequenceNum);
          eventBus.emit({
            type:        "persistence:cloud_synced",
            projectId:   c.projectId,
            sequenceNum: c.sequenceNum,
            durationMs,
          });
          eventBus.emit({ type: "persistence:ledger_updated", projectId: c.projectId, status: "synced" });
          // Phase 2.5: confirm canonical schema persisted to cloud storage
          if (payload.canonicalSchema) {
            eventBus.emit({
              type:        "persistence:cloud_canonical_saved",
              projectId:   c.projectId,
              fingerprint: payload.canonicalSchema.fingerprint,
            });
          }
          logger.info("PersistenceBridge: cloud sync success", { ...c, attempt, durationMs });
        },
        onFailure: (c, attempt, error, nextRetryMs) => {
          OfflineJournal.recordAttempt(c.projectId, c.sequenceNum, error);
          eventBus.emit({
            type:        "persistence:sync_failed",
            projectId:   c.projectId,
            sequenceNum: c.sequenceNum,
            error,
            attempt,
            nextRetryMs,
          });
        },
        onExhausted: (c, totalAttempts) => {
          SyncLedger.markFailed(c.projectId, `Exhausted after ${totalAttempts} attempts`);
          eventBus.emit({
            type:        "persistence:sync_exhausted",
            projectId:   c.projectId,
            sequenceNum: c.sequenceNum,
            totalAttempts,
          });
          eventBus.emit({ type: "persistence:ledger_updated", projectId: c.projectId, status: "failed" });
        },
      }
    );
  }

  /* ── OFFLINE HANDLING ──────────────────────────────────── */

  private handleOffline(): void {
    // Mark all pending projects as offline in ledger
    SyncLedger.getPending().forEach(e => SyncLedger.markOffline(e.projectId));
    this.queue.pause();
    logger.info("PersistenceBridge: offline - queue paused");
  }

  /* ── CRASH RECOVERY ────────────────────────────────────── */

  private async replayUnsyncedJournal(): Promise<void> {
    const unsynced = OfflineJournal.getAllUnsynced();
    if (unsynced.length === 0) return;

    logger.info("PersistenceBridge: replaying unsynced journal entries", { count: unsynced.length });
    eventBus.emit({ type: "persistence:journal_replaying", projectId: "__all__", pendingCount: unsynced.length });

    let enqueued = 0;
    for (const entry of unsynced) {
      let payload = entry.snapshot as ProjectContentPayload;
      if (!payload?.projectId) continue;

      // Migrate replayed payload to current schemaVersion before re-enqueueing.
      // Without this check, a payload written at schemaVersion 1 could be
      // replayed into a cloud that expects schemaVersion 2.
      if (payloadNeedsMigration(entry.schemaVersion)) {
        const fromVersion = entry.schemaVersion ?? 0;
        const result = runMigrations<ProjectContentPayload>({
          scope:       SCOPE_PAYLOAD,
          storageKey:  `nuvra-journal-replay-${entry.projectId}-seq${entry.sequenceNum}`,
          fromVersion,
          toVersion:   CURRENT_PAYLOAD_VERSION,
          data:        payload,
        });
        if (!result.success || !result.data) {
          logger.error("PersistenceBridge: journal replay migration failed - skipping entry", {
            projectId:   payload.projectId,
            sequenceNum: entry.sequenceNum,
            fromVersion,
          });
          continue;
        }
        payload = result.data;
      }

      this.queue.enqueue(payload);
      enqueued++;
    }

    eventBus.emit({ type: "persistence:journal_replayed", projectId: "__all__", replayedCount: enqueued });
  }
}

/* =========================================================
   SINGLETON + RUNTIME MODULE EXPORT
   ========================================================= */

const bridge = new PersistenceSyncBridge();

/** RuntimeModule definition for coreRuntime registration. */
export const persistenceSyncModule: RuntimeModule = {
  id:           "persistence-sync",
  name:         "Persistence Sync Bridge",
  dependencies: ["persistence", "state", "diagnostics"],
  init:         () => bridge.init(),
  teardown:     () => bridge.teardown(),
};

/** Public API for imperative flush (deploy actions, manual save, etc.). */
export const persistenceBridge = {
  flushNow: (reason: string) => bridge.flushNow(reason),
};
